// Staging overrides and A/B tests (spec/dashboard.md sections 8.3–8.4): a new override for a document's parameters, a new
// test with its variants, and a test's variant override — one OverrideInfo with a group per variant, each gated by an
// And [ ABTestCondition { test, variant } ] tree, the rows pre-filled with the current values; everything is a draft
// linked by temp ids, so one Save All commits it all (the shape Balancy saves, research/06 section 3.2).
import type { Document, Json } from '../../api/types';
import type { Workspace } from '../../workspace/Workspace';
import { CONDITION_NS } from '../conditions/model';
import type { ContentCatalog } from '../ContentContext';
import { createDocumentDraft } from '../widgets/context';
import { AB_CONDITION, AB_TEST, AB_VARIANT, OVERRIDE_INFO, toGroupJson, valueAt, type OverridableLeaf, type OverrideGroupData } from './model';

export interface StageEnv {
  workspace: Workspace;
  catalog: ContentCatalog;
}

function stage(env: StageEnv, template: string, data: Record<string, Json>): string {
  const t = env.catalog.templates.get(template);
  if (!t) throw new Error(`The branch has no ${template} template.`);
  return createDocumentDraft(env.workspace, t, data);
}

/** One OverrideData* component for a leaf of the target, carrying the given value (the current one by default). */
export function stageRow(env: StageEnv, target: Document, leaf: OverridableLeaf, value?: Json): string {
  const current = value === undefined ? (valueAt(target.data as Json, leaf.path) ?? null) : value;
  return stage(env, leaf.template, { document: target.id, path: leaf.path, value: current });
}

/** A new override (flag 0) with one group of the document's picked parameters; returns the override's temp id. */
export function createOverrideFor(env: StageEnv, target: Document, leaves: OverridableLeaf[]): string {
  const rows = leaves.map((leaf) => stageRow(env, target, leaf));
  const group: OverrideGroupData = { condition: null, overrides: rows, comments: '', priority: 100 };
  return stage(env, OVERRIDE_INFO, { name: `${env.catalog.label(target)} override`, flag: 0, groups: [toGroupJson(group)] });
}

/**
 * The variant override of a test for one document: a group per variant, each with its ABTestCondition tree; returns the
 * override's temp id. `valueOf(i, leaf)` gives variant i's value of a leaf (undefined: the current value).
 */
export function createVariantOverride(env: StageEnv, test: Document, variantIds: string[], target: Document, leaves: OverridableLeaf[], valueOf?: (variantIndex: number, leaf: OverridableLeaf) => Json | undefined): string {
  const groups: Json[] = variantIds.map((variant, i) => {
    const leafCondition = stage(env, AB_CONDITION, { test: test.id, variant });
    const root = stage(env, `${CONDITION_NS}And`, { conditions: [leafCondition] });
    const rows = leaves.map((leaf) => stageRow(env, target, leaf, valueOf?.(i, leaf)));
    return toGroupJson({ condition: root, overrides: rows, comments: '', priority: 100 });
  });
  const label = env.catalog.label(target);
  return stage(env, OVERRIDE_INFO, { name: `${label} - ${target.unnyId ?? target.id.slice(0, 8)}`, flag: 1, groups });
}

/** A variant of a new test: its name, weight and (for the wizard) its value per picked leaf path. */
export interface VariantSpec {
  name: string;
  weight: number;
  values?: Record<string, Json>;
}

/** What a new test is staged with (spec/dashboard.md 8.4, the A/B wizard); every field has the blank test's default. */
export interface AbTestSpec {
  name: string;
  variants: VariantSpec[];
  /** the root of the test's condition (staged by the caller, e.g. stageSegmentCondition), null = every player */
  condition: string | null;
  targetUsers: number;
  /** SmartObjects.GroupType: 0 New, 1 Old, 2 All */
  usersType: number;
  /** the planner (written only when the branch's ABTest template declares the parameter) */
  metric?: string;
  uplift?: number;
  alpha?: number;
  betta?: number;
  /** the required players over all variants and the expected new players a day (the template's totalUsers / usersPerDay) */
  totalUsers?: number;
  usersPerDay?: number;
}

const DEFAULT_VARIANTS: VariantSpec[] = [
  { name: 'Control', weight: 100 },
  { name: 'Variant A', weight: 100 },
];

/** Stages And [ SegmentCondition { segment } ] and answers the root's temp id (the test's "In a segment" audience). */
export function stageSegmentCondition(env: StageEnv, segmentId: string): string {
  const leaf = stage(env, `${CONDITION_NS}SegmentCondition`, { segment: segmentId });
  return stage(env, `${CONDITION_NS}And`, { conditions: [leaf] });
}

/** A new Inactive test with its variants; returns the test's temp id and the variant ids (Control + Variant A, weight 100, by default). */
export function createAbTest(env: StageEnv, name: string, spec: Partial<Omit<AbTestSpec, 'name'>> = {}): { testId: string; variantIds: string[] } {
  const variantIds = (spec.variants ?? DEFAULT_VARIANTS).map((v) => stage(env, AB_VARIANT, { name: v.name, weight: v.weight }));
  const declared = new Set((env.catalog.templates.get(AB_TEST)?.effectiveParameters ?? []).map((p) => p.name));
  const planner: Record<string, Json> = {};
  const put = (key: string, value: Json | undefined) => {
    if (value !== undefined && declared.has(key)) planner[key] = value;
  };
  put('metric', spec.metric);
  put('uplift', spec.uplift);
  put('alphaGlobal', spec.alpha);
  put('betta', spec.betta);
  put('totalUsers', spec.totalUsers);
  put('usersPerDay', spec.usersPerDay);
  const testId = stage(env, AB_TEST, {
    name,
    variants: variantIds,
    status: 0,
    targetUsers: spec.targetUsers ?? 100,
    usersType: spec.usersType ?? 2,
    condition: spec.condition ?? null,
    bestVariant: null,
    finishTime: 0,
    concurrentTest: false,
    preInitLaunch: false,
    salt: '',
    ...planner,
  });
  return { testId, variantIds };
}

/**
 * "Create A/B test…" on a document, and the A/B wizard: the test (Inactive) with its variants and, when a target is
 * given, its variant override of the picked parameters with each variant's values; returns the test's temp id.
 */
export function createAbTestFor(env: StageEnv, target: Document | null, leaves: OverridableLeaf[], spec: Partial<AbTestSpec> = {}): string {
  const { name, ...rest } = spec;
  const { testId, variantIds } = createAbTest(env, name?.trim() || (target ? `${env.catalog.label(target)} test` : 'New test'), rest);
  const test = env.catalog.resolve(testId);
  const variants = spec.variants ?? DEFAULT_VARIANTS;
  if (test && target && leaves.length > 0) createVariantOverride(env, test as Document, variantIds, target, leaves, (i, leaf) => variants[i]?.values?.[leaf.path]);
  return testId;
}

/** A new variant document for a test, appended to its `variants` list (one edit). */
export function addVariant(env: StageEnv, test: Document, edit: (path: string, value: Json) => void): string {
  const variants = Array.isArray((test.data as Record<string, Json>).variants) ? ((test.data as Record<string, Json>).variants as Json[]) : [];
  const letter = String.fromCharCode('A'.charCodeAt(0) + Math.max(0, variants.length - 1));
  const id = stage(env, AB_VARIANT, { name: `Variant ${letter}`, weight: 100 });
  edit('data.variants', [...variants, id]);
  return id;
}
